import { Controller } from 'react-hook-form';
import {
  ButtonSecondary,
  ButtonSize,
  IconExpand,
  IconLoader,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import { useAppSelector } from '@store';
import groupBy from 'lodash.groupby';
import { v4 as uuid } from 'uuid';
import InfoRow from '../info-row';
import {
  AccountsCurrenciesFormValues,
  NewApplicationStatus,
} from '../../../hoc/new-application/types';
import { IndividualApplicationStepTowTypes } from '../types';
import StepPartBlock from '../corporate/parts/step-part-block';
import AccountCurrency from '../corporate/parts/account-currency';
import AccountFlow from '../corporate/parts/account-flow';
import ExpectedFundsOrigins from '../corporate/parts/expected-funds-origins';

import cn from '../new-application.module.css';

const NewApplicationInfoStepTwo = ({
  control,
  status,
  isEditable,
  errors,
  clearErrors,
  questionnarieClearErrors,
  questionnarieControl,
  questionnarieErrors,
  questionnarieSetValue,
  questionnarieFields,
  accountsCurrenciesIsLoading,
  accountsCurrenciesIsFetching,
  currencies,
  accountsCurrenciesValues,
  setAccountsCurrenciesValues,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  setAccountsFlowsValues,
  accountsFlowsValues,
  customerSourceOfFunds,
  accountsFlowsIsLoading,
  accountsFlowsIsFetching,
  currenciesList,
  regulatedCompanyScheme,
  expectedFundsOriginsIsLoading,
  expectedFundsOriginsIsFetching,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOriginsValues,
  setExpectedFundsOriginsValues,
  countries,
}: IndividualApplicationStepTowTypes) => {
  const {
    monthlyTurnover,
    numberOfIncomingTransactions,
    numberOfOutgoingTransactions,
    valueOfIncomingTransactions,
    valueOfOutgoingTransactions,
    maxValueOfIncomingPayment,
    maxValueOfOutgoingPayment,
    productsIndividual,
    baseCurrency,
  } = useAppSelector((state) => state.customersDictionaries);

  const groupedAccountsFlows = groupBy(
    accountsFlowsValues,
    (match) => match.currencyId,
  );

  const incomingExpectedFundsOfOrigins = expectedFundsOriginsValues?.filter(
    (item) => item.transactionType === 'INCOMING',
  );
  const outgoingExpectedFundsOfOrigins = expectedFundsOriginsValues?.filter(
    (item) => item.transactionType === 'OUTGOING',
  );

  return (
    <div className={cn.stepBlockWrapper} style={{ zIndex: 19 }}>
      <div className={cn.stepBlockContent}>
        <StepPartBlock title="Monthly Volumes" zIndex={20}>
          <InfoRow
            title={`Monthly Turnover (${baseCurrency?.name})`}
            id="monthlyTurnoverRangeId"
            lessPadding
          >
            <Controller
              control={control}
              name="monthlyTurnoverRangeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="monthlyTurnoverRangeId"
                  placeholder="Select range"
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['monthlyTurnoverRangeId']);
                  }}
                  list={monthlyTurnover}
                  size={SelectSize.SMALL}
                  error={errors?.monthlyTurnoverRangeId?.message}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  borderless
                  fullWidth
                />
              )}
            />
          </InfoRow>

          <InfoRow
            title="N. of incoming transactions"
            id="monthlyNumberIncomingTransactionsRangeId"
            lessPadding
          >
            <Controller
              control={control}
              name="monthlyNumberIncomingTransactionsRangeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="monthlyNumberIncomingTransactionsRangeId"
                  placeholder="Select range"
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['monthlyNumberIncomingTransactionsRangeId']);
                  }}
                  list={numberOfIncomingTransactions}
                  size={SelectSize.SMALL}
                  error={
                    errors?.monthlyNumberIncomingTransactionsRangeId?.message
                  }
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title="N. of outgoing transactions"
            id="monthlyNumberOutgoingTransactionsRangeId"
            lessPadding
          >
            <Controller
              control={control}
              name="monthlyNumberOutgoingTransactionsRangeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="monthlyNumberOutgoingTransactionsRangeId"
                  placeholder="Select range"
                  list={numberOfOutgoingTransactions}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['monthlyNumberOutgoingTransactionsRangeId']);
                  }}
                  error={
                    errors?.monthlyNumberOutgoingTransactionsRangeId?.message
                  }
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title={`Value of incoming transactions (${baseCurrency?.name})`}
            id="monthlyValueIncomingTransactionsRangeId"
            lessPadding
          >
            <Controller
              control={control}
              name="monthlyValueIncomingTransactionsRangeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="monthlyValueIncomingTransactionsRangeId"
                  placeholder="Select range"
                  list={valueOfIncomingTransactions}
                  value={value}
                  error={
                    errors?.monthlyValueIncomingTransactionsRangeId?.message
                  }
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['monthlyValueIncomingTransactionsRangeId']);
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title={`Value of outgoing transactions (${baseCurrency?.name})`}
            id="monthlyValueOutgoingTransactionsRangeId"
            lessPadding
          >
            <Controller
              control={control}
              name="monthlyValueOutgoingTransactionsRangeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="monthlyValueOutgoingTransactionsRangeId"
                  placeholder="Select range"
                  list={valueOfOutgoingTransactions}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['monthlyValueOutgoingTransactionsRangeId']);
                  }}
                  error={
                    errors?.monthlyValueOutgoingTransactionsRangeId?.message
                  }
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title={`Max. value of incoming payment (${baseCurrency?.name})`}
            id="monthlyMaxValueIncomingPaymentRangeId"
            lessPadding
          >
            <Controller
              control={control}
              name="monthlyMaxValueIncomingPaymentRangeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="monthlyMaxValueIncomingPaymentRangeId"
                  placeholder="Select range"
                  list={maxValueOfIncomingPayment}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['monthlyMaxValueIncomingPaymentRangeId']);
                  }}
                  error={errors?.monthlyMaxValueIncomingPaymentRangeId?.message}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title={`Max. value of outgoing payment (${baseCurrency?.name})`}
            id="monthlyMaxValueOutgoingPaymentRangeId"
            lessPadding
          >
            <Controller
              control={control}
              name="monthlyMaxValueOutgoingPaymentRangeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="monthlyMaxValueOutgoingPaymentRangeId"
                  placeholder="Select range"
                  list={maxValueOfOutgoingPayment}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['monthlyMaxValueOutgoingPaymentRangeId']);
                  }}
                  error={errors?.monthlyMaxValueOutgoingPaymentRangeId?.message}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
        </StepPartBlock>
        {regulatedCompanyScheme && (
          <>
            <StepPartBlock
              title="Purpose of establishing a business relationship"
              zIndex={19}
            >
              <div className={cn.accountsCurrenciesList}>
                {questionnarieErrors?.accountsCurrencies?.message && (
                  <div className={cn.questionnarieError}>
                    {questionnarieErrors?.accountsCurrencies?.message}
                  </div>
                )}
                {!accountsCurrenciesIsLoading &&
                  !accountsCurrenciesIsFetching && (
                    <>
                      {!accountsCurrenciesValues?.length && (
                        <div className={cn.emptyAccountsCurrenciesList}>
                          No currencies added yet
                        </div>
                      )}
                      {accountsCurrenciesValues?.map((item, index) => (
                        <AccountCurrency
                          key={item.id}
                          accountCurrency={item}
                          setAccountsCurrenciesValues={
                            setAccountsCurrenciesValues
                          }
                          currencies={currencies}
                          index={index}
                          isEditable={isEditable}
                          status={status}
                          accountsCurrencies={accountsCurrenciesValues}
                          onDeleteAccountCurrencies={onDeleteAccountCurrencies}
                          selectedAccountsCurrencyId={
                            selectedAccountsCurrencyId
                          }
                          deleteAccountsCurrenciesInProgress={
                            deleteAccountsCurrenciesInProgress
                          }
                          setAccountsFlowsValues={setAccountsFlowsValues}
                          accountsFlowsValues={accountsFlowsValues}
                        />
                      ))}
                      {currencies &&
                        currencies?.filter((item) =>
                          accountsCurrenciesValues?.every(
                            (accountCurrency) =>
                              item.value !== accountCurrency.currencyId,
                          ),
                        )?.length > 0 && (
                          <ButtonSecondary
                            text="Add currency"
                            icon={<IconExpand />}
                            className={`button-white ${cn.addAccountCurrencyButton}`}
                            size={ButtonSize.SMALL}
                            onClick={() => {
                              let nextAccountsCurrencies: AccountsCurrenciesFormValues[] =
                                [];

                              if (accountsCurrenciesValues) {
                                nextAccountsCurrencies = (
                                  [] as AccountsCurrenciesFormValues[]
                                ).concat(accountsCurrenciesValues);
                              }

                              nextAccountsCurrencies.push({
                                id: `accounts-currency-${uuid()}`,
                                currencyId: '',
                                plannedTransactionNature: '',
                                accountOpeningPurpose: '',
                              });

                              setAccountsCurrenciesValues(
                                nextAccountsCurrencies,
                              );
                            }}
                          />
                        )}
                    </>
                  )}
                {(accountsCurrenciesIsLoading ||
                  accountsCurrenciesIsFetching) && (
                  <div className={cn.accountsCurrenciesLoader}>
                    <IconLoader />
                  </div>
                )}
              </div>
            </StepPartBlock>
            <StepPartBlock title="Source of funds" zIndex={18}>
              <InfoRow
                title="Source of funds to be credited to the account"
                id="sourcesOfFundsIds"
                style={{
                  position: 'relative',
                  zIndex: 10,
                }}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="sourcesOfFundsIds"
                  defaultValue={[]}
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select an option..."
                      list={
                        customerSourceOfFunds?.map((item) => ({
                          name: item.value,
                          value: item.id,
                        })) || []
                      }
                      value={value}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['sourcesOfFundsIds']);

                        if (
                          !(e.target.value as string[])?.includes('Other') &&
                          questionnarieFields?.sourcesOfFundsOther
                        ) {
                          questionnarieClearErrors(['sourcesOfFundsOther']);
                          questionnarieSetValue('sourcesOfFundsOther', '');
                        }

                        if (
                          !(e.target.value as string[])?.includes('Other') &&
                          questionnarieFields?.sourcesOfFundsLoanCreditInstitution
                        ) {
                          questionnarieClearErrors([
                            'sourcesOfFundsLoanCreditInstitution',
                          ]);
                          questionnarieSetValue(
                            'sourcesOfFundsLoanCreditInstitution',
                            '',
                          );
                        }

                        if (
                          !(e.target.value as string[])?.includes('Other') &&
                          questionnarieFields?.sourcesOfFundsLoanBusinessPartner
                        ) {
                          questionnarieClearErrors([
                            'sourcesOfFundsLoanBusinessPartner',
                          ]);
                          questionnarieSetValue(
                            'sourcesOfFundsLoanBusinessPartner',
                            '',
                          );
                        }
                      }}
                      selectWrapperClassName={
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.selectDisabled
                          : ''
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={questionnarieErrors?.sourcesOfFundsIds?.message}
                      mode={SelectType.MULTIPLE}
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
                customerSourceOfFunds
                  ?.find((client) => item === client.id)
                  ?.value?.includes('Other'),
              ) && (
                <InfoRow
                  title="Provide details"
                  id="sourcesOfFundsOther"
                  className={cn.noFlex}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="sourcesOfFundsOther"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Textarea
                        name={name}
                        title=""
                        placeholder="Enter details..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['sourcesOfFundsOther']);
                        }}
                        className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                        error={
                          questionnarieErrors?.sourcesOfFundsOther?.message
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
                customerSourceOfFunds
                  ?.find((client) => item === client.id)
                  ?.value?.includes('Loan from a credit institution'),
              ) && (
                <InfoRow
                  title="Credit institution which have provided loan"
                  id="sourcesOfFundsLoanCreditInstitution"
                  className={cn.noFlex}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="sourcesOfFundsLoanCreditInstitution"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Textarea
                        name={name}
                        placeholder="Enter details..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors([
                            'sourcesOfFundsLoanCreditInstitution',
                          ]);
                        }}
                        className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={
                          questionnarieErrors
                            ?.sourcesOfFundsLoanCreditInstitution?.message
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
                customerSourceOfFunds
                  ?.find((client) => item === client.id)
                  ?.value?.includes('Loan from a business partner'),
              ) && (
                <InfoRow
                  title="Business partner which provided loan"
                  id="sourcesOfFundsLoanBusinessPartner"
                  className={cn.noFlex}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="sourcesOfFundsLoanBusinessPartner"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Textarea
                        name={name}
                        placeholder="Enter details..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors([
                            'sourcesOfFundsLoanBusinessPartner',
                          ]);
                        }}
                        className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={
                          questionnarieErrors
                            ?.sourcesOfFundsLoanCreditInstitution?.message
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              <InfoRow
                title="Does the Customer have accounts in other financial institutions?"
                id="hasAccountsInOtherFinancialInstitutions"
                style={{
                  position: 'relative',
                  zIndex: 9,
                }}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="hasAccountsInOtherFinancialInstitutions"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select an option..."
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'hasAccountsInOtherFinancialInstitutions',
                        ]);

                        if (
                          e.target.value === 'no' &&
                          questionnarieFields?.otherFinancialInstitutions
                        ) {
                          questionnarieClearErrors([
                            'otherFinancialInstitutions',
                          ]);
                          questionnarieSetValue(
                            'otherFinancialInstitutions',
                            '',
                          );
                        }
                      }}
                      selectWrapperClassName={
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.selectDisabled
                          : ''
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={
                        questionnarieErrors
                          ?.hasAccountsInOtherFinancialInstitutions?.message
                      }
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              {questionnarieFields?.hasAccountsInOtherFinancialInstitutions ===
                'yes' && (
                <InfoRow
                  title="Financial institutions at which customer has accounts"
                  id="otherFinancialInstitutions"
                  className={cn.noFlex}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="otherFinancialInstitutions"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Textarea
                        name={name}
                        title=""
                        placeholder="Enter details..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors([
                            'otherFinancialInstitutions',
                          ]);
                        }}
                        className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                        error={
                          questionnarieErrors?.otherFinancialInstitutions
                            ?.message
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
            </StepPartBlock>
            <StepPartBlock title="Expected flows on own account" zIndex={17}>
              {!accountsFlowsIsLoading &&
                !accountsFlowsIsFetching &&
                accountsFlowsValues &&
                accountsFlowsValues?.length > 0 && (
                  <div className={cn.expectedFlowsHint}>
                    Currencies selected in the&nbsp;
                    <strong>
                      &quot;Purpose for establishing business relationship&quot;
                    </strong>
                    &nbsp; section
                  </div>
                )}

              <div className={cn.accountsCurrenciesList}>
                {questionnarieErrors?.accountsFlows?.message && (
                  <div className={cn.questionnarieError}>
                    {questionnarieErrors?.accountsFlows?.message}
                  </div>
                )}
                {(accountsFlowsIsLoading || accountsFlowsIsFetching) && (
                  <div className={cn.accountsCurrenciesLoader}>
                    <IconLoader />
                  </div>
                )}
                {!accountsFlowsValues?.length && (
                  <div className={cn.emptyAccountsFlows}>
                    Add currencies in&nbsp;
                    <strong>
                      &quot;Purpose for establishing business relationship&quot;
                    </strong>
                  </div>
                )}
                {!accountsFlowsIsLoading &&
                  !accountsFlowsIsFetching &&
                  Object.keys(groupedAccountsFlows)?.map((group) =>
                    groupedAccountsFlows[group]?.map((flow) => (
                      <AccountFlow
                        key={flow.id}
                        accountFlow={flow}
                        accountsFlows={accountsFlowsValues}
                        setAccountsFlowsValues={setAccountsFlowsValues}
                        currencies={currencies}
                        isEditable={isEditable}
                        status={status}
                        currenciesList={currenciesList}
                      />
                    )),
                  )}
              </div>
            </StepPartBlock>
            <StepPartBlock title="Expected origin of funds" zIndex={16}>
              <div className={cn.accountsCurrenciesList}>
                {(expectedFundsOriginsIsLoading ||
                  expectedFundsOriginsIsFetching) && (
                  <div className={cn.accountsCurrenciesLoader}>
                    <IconLoader />
                  </div>
                )}
                {questionnarieErrors?.questionnairesExpectedFundsOrigins
                  ?.message && (
                  <div className={cn.questionnarieError}>
                    {
                      questionnarieErrors?.questionnairesExpectedFundsOrigins
                        ?.message
                    }
                  </div>
                )}
                {!accountsFlowsValues?.length && (
                  <div className={cn.emptyAccountsFlows}>
                    Add currencies in&nbsp;
                    <strong>
                      &quot;Purpose for establishing business relationship&quot;
                    </strong>
                  </div>
                )}

                {accountsFlowsValues &&
                  accountsFlowsValues?.length > 0 &&
                  !expectedFundsOriginsIsLoading &&
                  !expectedFundsOriginsIsFetching && (
                    <>
                      <ExpectedFundsOrigins
                        status={status}
                        isEditable={isEditable}
                        currencies={
                          accountsCurrenciesValues?.map((item) => ({
                            name: currencies?.find(
                              (currency) => currency.value === item.currencyId,
                            )?.name as string,
                            suffix: currencies?.find(
                              (currency) => currency.value === item.currencyId,
                            )?.suffix as string,
                            value: item.currencyId,
                          })) || []
                        }
                        deleteExpectedFundsOriginsInProgress={
                          deleteExpectedFundsOriginsInProgress
                        }
                        onDeleteExpectedFundsOrigins={
                          onDeleteExpectedFundsOrigins
                        }
                        selectedExpectedFundsOriginsId={
                          selectedExpectedFundsOriginsId
                        }
                        expectedFundsOriginsValues={
                          incomingExpectedFundsOfOrigins
                        }
                        expectedFundsOriginsValuesList={
                          expectedFundsOriginsValues
                        }
                        setExpectedFundsOriginsValues={
                          setExpectedFundsOriginsValues
                        }
                        countries={countries}
                        type="INCOMING"
                      />
                      <ExpectedFundsOrigins
                        status={status}
                        isEditable={isEditable}
                        currencies={
                          accountsCurrenciesValues?.map((item) => ({
                            name: currencies?.find(
                              (currency) => currency.value === item.currencyId,
                            )?.name as string,
                            suffix: currencies?.find(
                              (currency) => currency.value === item.currencyId,
                            )?.suffix as string,
                            value: item.currencyId,
                          })) || []
                        }
                        deleteExpectedFundsOriginsInProgress={
                          deleteExpectedFundsOriginsInProgress
                        }
                        onDeleteExpectedFundsOrigins={
                          onDeleteExpectedFundsOrigins
                        }
                        selectedExpectedFundsOriginsId={
                          selectedExpectedFundsOriginsId
                        }
                        expectedFundsOriginsValues={
                          outgoingExpectedFundsOfOrigins
                        }
                        expectedFundsOriginsValuesList={
                          expectedFundsOriginsValues
                        }
                        setExpectedFundsOriginsValues={
                          setExpectedFundsOriginsValues
                        }
                        countries={countries}
                        type="OUTGOING"
                      />
                    </>
                  )}
              </div>
            </StepPartBlock>
          </>
        )}
        <StepPartBlock title="Products & Services" zIndex={15}>
          <Controller
            control={control}
            name="productIds"
            render={({ field: { value } }) => (
              <input type="hidden" value={value} name="productIds" />
            )}
          />
          <InfoRow title="Products" id="productIds" lessPadding>
            <Controller
              control={control}
              name="productIds"
              defaultValue={[]}
              render={({ field: { onChange, value } }) => (
                <Select
                  name="productIds"
                  placeholder="Select products"
                  list={productsIndividual}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['productIds', 'products']);
                  }}
                  mode={SelectType.MULTIPLE}
                  error={
                    errors?.productIds?.message || errors?.products?.message
                  }
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  borderless
                />
              )}
            />
          </InfoRow>
        </StepPartBlock>
      </div>
    </div>
  );
};

export default NewApplicationInfoStepTwo;
